import { formatBytes } from '../format/sizeFormat';
import type { IndexAnalysisResult, IndexIssue, IndexRecommendation } from '../models/indexAnalysisResults.ts';
import type { Index } from '../models/indexes';
import MultiValueMap from '../multiValueMap';
import { DimensionTree } from './lib/dimensionTree';
import { sum } from 'radash';

export default function findRedundantIndexes(indexes: Index[]): IndexAnalysisResult {
  const issues = new MultiValueMap<string, IndexIssue>();
  const recommendations: IndexRecommendation[] = [];
  const redundantIndexes = new Array<string>();

  const indexesWithTrees = indexes.map((index) => ({
    name: index.spec.name ?? '',
    index,
    tree: DimensionTree.fromIndex(index),
  }));
  const commonTree = DimensionTree.mergeTrees(indexesWithTrees.map((item) => item.tree));

  for (const { name, index, tree } of indexesWithTrees) {
    if (index.implicit || index.spec.expireAfterSeconds !== undefined) {
      continue;
    }

    const restTree = commonTree.without([name]);

    let coveredBy;
    if ((coveredBy = restTree.covers(tree))) {
      issues.add(name, {
        type: 'redundant',
        message: `Is covered by ${Array.from(coveredBy).join(', ')}`,
        coveredBy: Array.from(coveredBy),
      });
      redundantIndexes.push(name);
    }
  }

  function visit(dt: DimensionTree, excluded: string[], rest: string[], size: number): [string[], number] | undefined {
    if (excluded.length > 1) {
      optionsCalculated++;
      dt = dt.without(excluded);
      if (!dt.covers(commonTree)) {
        return undefined;
      }
    }

    let max: [string[], number] | undefined;

    rest.forEach((name, i) => {
      const addedSize = indexSize(name);
      const result = visit(dt, [...excluded, name], rest.slice(i + 1), size + addedSize);
      if (result) {
        if (!max || result[1] > max[1]) {
          max = result;
        }
      }
    });

    if (max) {
      return max;
    }

    return [excluded, size];
  }

  function indexSize(name: string): number {
    return sum(indexes.find((index) => index.name === name)?.actual.flatMap((a) => a.size) ?? []);
  }

  let optionsCalculated = 0;
  const startTime = performance.now();
  const result = visit(commonTree, [], redundantIndexes, 0);

  if (result?.[1]) {
    const savedSize = formatBytes(result[1]);

    recommendations.push({
      type: 'removeRedundant',
      message: `Remove redundant indexes to save ${savedSize}:\n${result[0].map((name) => `- ${name}`).join('\n')}`,
      removeIndexes: result[0],
      savedSize: result[1],
      savedSizePerIndex: new Map(result[0].map((name) => [name, indexSize(name)])),
      debug: {
        optionsConsidered: optionsCalculated,
        timeMs: performance.now() - startTime,
      },
    });
  }

  return {
    issues,
    recommendations,
  };
}
